import React, { useEffect, useState } from 'react';
import { SectionWrapper } from './SectionWrapper';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { Calculator, AlertCircle } from 'lucide-react';
export function InteractiveCalculator() {
  // Default values based on the worked example
  const [gmv, setGmv] = useState(45000);
  const [cov, setCov] = useState(0.18);
  const [trend, setTrend] = useState(8);
  const [hhi, setHhi] = useState('Moderate'); // Low, Moderate, High
  const [tenure, setTenure] = useState(14);
  const [cbRate, setCbRate] = useState(0.3);
  const [results, setResults] = useState({
    score: 0,
    tier: '',
    multiplier: 0,
    ceiling: 0,
    holdback: '',
    breakdown: [] as any[]
  });
  useEffect(() => {
    // 1. Calculate individual scores (0-100)
    // GMV: Scaled so 62.5k = 100 pts (45k = 72 pts)
    const gmvScore = Math.min(100, Math.max(0, gmv / 62500 * 100));
    // CoV: <0.20 = 100, >0.50 = 0. Linear interpolation.
    // Formula to match example: 0.18 -> 82. Let's use 100 - (CoV * 100)
    const covScore = Math.min(100, Math.max(0, 100 - cov * 100));
    // Trend: 8% -> 75. Formula: 50 + (trend * 3.125)
    const trendScore = Math.min(100, Math.max(0, 50 + trend * 3.125));
    // HHI: Low=90, Moderate=60, High=30
    const hhiScore = hhi === 'Low' ? 90 : hhi === 'Moderate' ? 60 : 30;
    // Tenure: 14 -> 68. Formula: (tenure/24)*100 + 10
    const tenureScore = Math.min(100, Math.max(0, tenure / 24 * 100 + 10));
    // CB Rate: 0.3 -> 88. Formula: 100 - (cbRate * 40)
    const cbScore = Math.min(100, Math.max(0, 100 - cbRate * 40));
    // 2. Apply weights
    const breakdown = [{
      name: 'Avg GMV',
      weight: 0.3,
      score: gmvScore,
      pts: gmvScore * 0.3
    }, {
      name: 'CoV',
      weight: 0.25,
      score: covScore,
      pts: covScore * 0.25
    }, {
      name: 'Trend',
      weight: 0.2,
      score: trendScore,
      pts: trendScore * 0.2
    }, {
      name: 'Diversity',
      weight: 0.15,
      score: hhiScore,
      pts: hhiScore * 0.15
    }, {
      name: 'Tenure',
      weight: 0.1,
      score: tenureScore,
      pts: tenureScore * 0.1
    }, {
      name: 'Op Quality',
      weight: 0.1,
      score: cbScore,
      pts: cbScore * 0.1
    }];
    const totalScore = breakdown.reduce((sum, item) => sum + item.pts, 0);
    const finalScore = Math.min(100, totalScore);
    // 3. Determine Tier
    let tier = '';
    let multiplier = 0;
    let holdback = '';
    if (finalScore >= 85) {
      tier = 'Platinum (A)';
      multiplier = 1.5;
      holdback = '8-10%';
    } else if (finalScore >= 70) {
      tier = 'Gold (B)';
      multiplier = 1.0;
      holdback = '10-12%';
    } else if (finalScore >= 55) {
      tier = 'Silver (C)';
      multiplier = 0.6;
      holdback = '12-15%';
    } else if (finalScore >= 40) {
      tier = 'Bronze (D)';
      multiplier = 0.35;
      holdback = '15-18%';
    } else {
      tier = 'Decline';
      multiplier = 0;
      holdback = 'N/A';
    }
    setResults({
      score: finalScore,
      tier,
      multiplier,
      ceiling: gmv * multiplier,
      holdback,
      breakdown
    });
  }, [gmv, cov, trend, hhi, tenure, cbRate]);
  // Gauge Chart Data
  const gaugeData = [{
    name: 'Score',
    value: results.score,
    fill: results.score >= 70 ? '#10b981' : results.score >= 55 ? '#f59e0b' : results.score >= 40 ? '#f97316' : '#ef4444'
  }, {
    name: 'Remaining',
    value: 100 - results.score,
    fill: '#334155'
  }];
  return <SectionWrapper title="Interactive Scorecard Calculator" subtitle="Adjust the merchant parameters below to see how the decisioning engine computes the final score, tier, and loan ceiling in real-time." data-id="element-89">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-90">
        {/* Input Form */}
        <div className="lg:col-span-7 bg-white rounded-xl border border-slate-200 shadow-sm p-6 md:p-8" data-id="element-91">
          <div className="flex items-center gap-2 mb-6 border-b border-slate-100 pb-4" data-id="element-92">
            <Calculator className="w-5 h-5 text-indigo-600" data-id="element-93" />
            <h3 className="text-lg font-semibold text-slate-900" data-id="element-94">
              Merchant Parameters
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-95">
            <div data-id="element-96">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-97">
                Avg Monthly GMV (SGD)
              </label>
              <input type="number" value={gmv} onChange={e => setGmv(Number(e.target.value))} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all" data-id="element-98" />
            </div>
            <div data-id="element-99">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-100">
                Revenue CoV (0.0 - 1.0)
              </label>
              <input type="number" step="0.01" value={cov} onChange={e => setCov(Number(e.target.value))} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all" data-id="element-101" />
            </div>
            <div data-id="element-102">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-103">
                MoM Trend (%)
              </label>
              <input type="number" value={trend} onChange={e => setTrend(Number(e.target.value))} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all" data-id="element-104" />
            </div>
            <div data-id="element-105">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-106">
                Customer Diversity (HHI)
              </label>
              <select value={hhi} onChange={e => setHhi(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all bg-white" data-id="element-107">
                <option value="Low" data-id="element-108">Low Concentration (Best)</option>
                <option value="Moderate" data-id="element-109">Moderate Concentration</option>
                <option value="High" data-id="element-110">High Concentration (Risk)</option>
              </select>
            </div>
            <div data-id="element-111">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-112">
                PSP Tenure (Months)
              </label>
              <input type="number" value={tenure} onChange={e => setTenure(Number(e.target.value))} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all" data-id="element-113" />
            </div>
            <div data-id="element-114">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-115">
                Chargeback Rate (%)
              </label>
              <input type="number" step="0.1" value={cbRate} onChange={e => setCbRate(Number(e.target.value))} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all" data-id="element-116" />
            </div>
          </div>

          {/* Hard Gates Check */}
          {(gmv < 10000 || cbRate > 1.0 || tenure < 6) && <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-id="element-117">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-118" />
              <div data-id="element-119">
                <h4 className="text-sm font-bold text-red-900" data-id="element-120">
                  Hard Gate Failure
                </h4>
                <p className="text-xs text-red-700 mt-1" data-id="element-121">
                  This merchant would be auto-declined before scoring due to:
                  {gmv < 10000 && ' GMV < 10k.'}
                  {cbRate > 1.0 && ' CB Rate > 1.0%.'}
                  {tenure < 6 && ' Tenure < 6 months.'}
                </p>
              </div>
            </div>}
        </div>

        {/* Results Panel */}
        <div className="lg:col-span-5 bg-slate-900 rounded-xl shadow-xl p-6 md:p-8 text-white flex flex-col" data-id="element-122">
          <h3 className="text-lg font-semibold text-slate-200 mb-6 text-center" data-id="element-123">
            Decision Engine Output
          </h3>

          <div className="relative h-48 w-full flex items-center justify-center mb-2" data-id="element-124">
            <ResponsiveContainer width="100%" height="100%" data-id="element-125">
              <PieChart data-id="element-126">
                <Pie data={gaugeData} cx="50%" cy="100%" startAngle={180} endAngle={0} innerRadius={80} outerRadius={110} paddingAngle={0} dataKey="value" stroke="none" data-id="element-127">
                  {gaugeData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} data-id="element-128" />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute bottom-0 left-0 w-full text-center pb-2" data-id="element-129">
              <span className="text-5xl font-bold tracking-tighter" data-id="element-130">
                {results.score.toFixed(1)}
              </span>
              <span className="text-slate-400 text-sm ml-1" data-id="element-131">/ 100</span>
            </div>
          </div>

          <div className="bg-slate-800 rounded-lg p-4 mb-6 border border-slate-700" data-id="element-132">
            <div className="flex justify-between items-center mb-2" data-id="element-133">
              <span className="text-slate-400 text-sm" data-id="element-134">Assigned Tier</span>
              <span className={`font-bold ${results.tier.includes('Decline') ? 'text-red-400' : 'text-amber-400'}`} data-id="element-135">
                {results.tier}
              </span>
            </div>
            <div className="flex justify-between items-center mb-2" data-id="element-136">
              <span className="text-slate-400 text-sm" data-id="element-137">Loan Ceiling</span>
              <span className="font-mono font-bold text-white" data-id="element-138">
                SGD {results.ceiling.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center" data-id="element-139">
              <span className="text-slate-400 text-sm" data-id="element-140">Suggested Holdback</span>
              <span className="font-mono text-indigo-300" data-id="element-141">
                {results.holdback}
              </span>
            </div>
          </div>

          <div className="flex-1" data-id="element-142">
            <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3" data-id="element-143">
              Score Breakdown
            </h4>
            <div className="space-y-2" data-id="element-144">
              {results.breakdown.map((item, i) => <div key={i} className="flex items-center justify-between text-sm" data-id="element-145">
                  <span className="text-slate-400" data-id="element-146">{item.name}</span>
                  <div className="flex items-center gap-3" data-id="element-147">
                    <span className="text-slate-500 text-xs w-8 text-right" data-id="element-148">
                      {Math.round(item.score)}
                    </span>
                    <span className="font-mono text-indigo-400 w-10 text-right" data-id="element-149">
                      {item.pts.toFixed(1)}
                    </span>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>;
}